/**
 * Offline-Hinweis als schließbares Info-Modal — ersetzt das frühere dauerhafte
 * Warnbanner. Der persistente Status lebt im Header (OfflineIndicator); das
 * Modal öffnet nur beim Übergang online → offline und lässt sich wegklicken.
 */

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconRefresh, IconWifiOff } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

export function OfflineInfoModal() {
  const { isOnline } = useOnlineStatus();
  const [opened, setOpened] = useState(false);
  const [checking, setChecking] = useState(false);
  const wasOnline = useRef(true);

  useEffect(() => {
    // Nur beim Verbindungsverlust öffnen — wer das Modal schließt, soll nicht
    // bei jedem Re-Render erneut unterbrochen werden.
    if (!isOnline && wasOnline.current) setOpened(true);
    if (isOnline) setOpened(false);
    wasOnline.current = isOnline;
  }, [isOnline]);

  const handleRetry = async () => {
    setChecking(true);
    try {
      await fetch('/api/ping', { method: 'HEAD', cache: 'no-store' });
      // Ping ging durch — Verbindung ist zurück, auch wenn das Browser-Event
      // noch nicht gefeuert hat.
      setOpened(false);
    } catch {
      /* weiterhin offline — Modal bleibt offen */
    } finally {
      setChecking(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      centered
      title={
        <Group gap="xs">
          <IconWifiOff size={18} color="var(--mantine-color-orange-6)" />
          <Text fw={600}>Offline-Modus</Text>
        </Group>
      }
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Keine Internetverbindung. Änderungen werden lokal gespeichert und automatisch
          synchronisiert, sobald Sie wieder online sind. Den aktuellen Status sehen Sie
          jederzeit oben rechts im Kopfbereich.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" size="xs" onClick={() => setOpened(false)}>
            Verstanden
          </Button>
          <Button
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={checking}
            onClick={handleRetry}
          >
            Verbindung erneut prüfen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
